import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { Button, Stack } from "@mui/material";
import DonutChart from "./donutchart";
import TableDonutChart from "./tableDonutChart";
import { Link, useNavigate } from "react-router-dom";
import TablePagination from "@mui/material/TablePagination";
import Pagination from "@mui/material/Pagination";
import moment from "moment";
import get from "lodash/get";
import CaseStatusLabel from "./CaseStatusLabel";
import Box from "./base/Box";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Case Name",
    align: "left",
  },
  {
    id: "type",
    numeric: true,
    disablePadding: false,
    label: "Case Type",
    align: "center",
  },
  {
    id: "owner",
    numeric: true,
    disablePadding: false,
    label: "Case Owner",
    align: "center",
  },
  {
    id: "status",
    numeric: true,
    disablePadding: false,
    label: "Status",
    align: "center",
  },
  // {
  //   id: "score",
  //   numeric: true,
  //   disablePadding: false,
  //   label: "Sentiment Score",
  // },
  // {
  //   id: "label",
  //   numeric: true,
  //   disablePadding: false,
  //   label: "Sentiment",
  // },
  {
    id: "no",
    numeric: true,
    disablePadding: false,
    label: "No. of Docs",
    align: "center",
  },
  {
    id: "date",
    numeric: true,
    disablePadding: false,
    label: "Creation Date",
    align: "left",
    align: "center",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: "#0065c91A" }}>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              hideSortIcon
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;
  const { totalRows } = props;

  return (
    <Toolbar>
      <Typography
        sx={{ flex: "1 1 100%", marginLeft: -1 }}
        variant="p"
        id="tableTitle"
        component="div"
      >
        {`Showing ${numSelected} of ${totalRows} legal discoveries`}
      </Typography>

      <Tooltip title="Filter list">
        <Button className="filterbtn">
          <FilterListIcon className="caseAddIcn" /> Filters
        </Button>
      </Tooltip>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function EnhancedTable(props) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const rows = [...props.rowData];
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > props.currentPage - 1) {
      if (props.totalPages > props.currentPage) {
        props.getPage(newPage + 1);
      }
    } else {
      props.getPage(newPage + 1);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    // setRowsPerPage(parseInt(event.target.value, 10));
    // setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    props.currentPage > 0
      ? Math.max(0, (1 + props.currentPage) * rowsPerPage - rows.length)
      : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: "18px",
          boxShadow: "0px 5px 15px #00000029",
        }}
      >
        <EnhancedTableToolbar
          numSelected={rows.length}
          totalRows={props.totalRows}
        />
        <TableContainer>
          <Table>
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.sort(getComparator(order, orderBy)).slice() */}
              {stableSort(rows, getComparator(order, orderBy))
                // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.name);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  const date = moment(get(row, "date"), "DD/MM/YYYY,hh:mm A")
                    .local()
                    .format("DD/MM/YYYY,hh:mm A");
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={`${row.name}_${row.id}_${index}`}
                      selected={isItemSelected}
                      onClick={() => {
                        navigate(`/case/detail/${row.id}`);
                      }}
                      sx={{
                        "&.MuiTableRow-root:hover": {
                          backgroundColor: "#0065c933",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell align="left"><span style={{fontWeight: 'bold'}}>{row.name}</span></TableCell>
                      <TableCell align="center"><span>{row.type}</span></TableCell>
                      <TableCell align="center">
                        <span>{row.owner ? row.owner : "-"}</span>
                      </TableCell>
                      <TableCell align="center">
                        <CaseStatusLabel status={row.status} sx={{margin: 'auto'}} />
                      </TableCell>
                      {/* <TableCell align="center" style={{ width: 100 }}> */}
                      {/*   {Math.sign(row.score) == -1 ? ( */}
                      {/*     <TableDonutChart */}
                      {/*       data={[ */}
                      {/*         (row.score.toFixed(1) * 50 * -1) - 100, */}
                      {/*         row.score.toFixed(1) * 50 * -1, */}
                      {/*       ]} */}
                      {/*       colors={["#edc7d0", "#D12953"]} */}
                      {/*       text={`${row.score.toFixed(1)}`} */}
                      {/*       textColor="#D12953" */}
                      {/*     /> */}
                      {/*   ) : ( */}
                      {/*     <TableDonutChart */}
                      {/*       data={[ */}
                      {/*         (row.score.toFixed(1) * 50) - 100, */}
                      {/*         row.score.toFixed(1) * 50 */}
                      {/*       ]} */}
                      {/*       colors={["#c9f3de", "#14804A"]} */}
                      {/*       text={ row.score.toFixed(1) == '0.0' ? ` ${row.score.toFixed(1)}` : `+${row.score.toFixed(1)}`} */}
                      {/*       textColor="#14804A" */}
                      {/*     /> */}
                      {/*   )} */}
                      {/* </TableCell> */}
                      <TableCell
                        align="center"
                        style={{ fontSize: 25, fontWeight: "bold" }}
                      >
                        {row.no}
                      </TableCell>
                      <TableCell align="center">
                        <span>
                        {date.split(" ")[0]}
                        <br />
                        {date.split(" ")[1]}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={props.totalRows}
            rowsPerPage={rows.length}
            page={props.currentPage - 1}
            onPageChange={handleChangePage}
            labelDisplayedRows={(data) => {
              if (props.currentPage === props.totalPages) {
                return `${data.count - rows.length + 1}-${data.count} of ${
                  data.count
                }`;
              } else {
                return `${data.from}-${data.to} of ${data.count}`;
              }
            }}
            nextIconButtonProps={
              props.totalPages > props.currentPage
                ? undefined
                : {
                    disabled: true,
                  }
            }
          />
        </TableContainer>
      </Paper>
    </Box>
  );
}
